import { LGraphNode, LiteGraph } from "litegraph.js";

export class TimingFunctionNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Timing";

    this.properties = {
      functionName: this.getUrlParam("easing") || "linear",
      duration: parseInt(this.getUrlParam("duration")) || 300,
      loop: this.getUrlParam("loop") === "false" ? false : true,
      play: this.getUrlParam("play") === "false" ? false : true,
    };

    this._hasStarted = false;
    this._startTime = 0;
    this._pausedTime = 0;

    this.easingFunctions = {
      linear: (t) => t,
      easeInQuad: (t) => t * t,
      easeOutQuad: (t) => t * (2 - t),
      easeInOutSine: (t) => -(Math.cos(Math.PI * t) - 1) / 2,
    };

    this.addWidget("combo", "Easing", this.properties.functionName, (val) => {
      this.properties.functionName = val;
      this.updateUrlParam("easing", val);
    }, { values: ["linear", "easeInQuad", "easeOutQuad", "easeInOutSine"] });

    this.addWidget("slider", "Duration", this.properties.duration, (val) => {
      this.properties.duration = Math.round(val);
      this._hasStarted = false;
      this.updateUrlParam("duration", Math.round(val));
    }, { min: 100, max: 5000 });

    this.addWidget("toggle", "Loop", this.properties.loop, (val) => {
      this._hasStarted = false;
      this.properties.loop = val;
      this.updateUrlParam("loop", val);
    });

    this.addWidget("toggle", "Play", this.properties.play, (val) => {
      this.properties.play = val;
      this.updateUrlParam("play", val);
    });

    this.addInput("Duration", "number");
    this.addOutput("Time", "number");

    this.size = [200, 200];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  getUrlParam(param) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  updateUrlParam() {}

  onDrawForeground(ctx) {
    if (!this._hasStarted) return;

    const { duration, play, loop, functionName } = this.properties;
    const easingFn = this.easingFunctions[functionName] || this.easingFunctions.linear;

    let elapsed = play ? performance.now() - this._startTime : this._pausedTime;
    if (loop) elapsed = elapsed % duration;
    else elapsed = Math.min(elapsed, duration);

    const normalized = Math.min(1, elapsed / duration);
    const w = this.size[0];
    const h = this.size[1];

    // Draw curve in the bottom area only (below widgets)
    const curveTop = h - 35;
    const curveH = 25;
    const pad = 8;

    // Curve background
    ctx.fillStyle = "rgba(0,0,0,0.2)";
    ctx.fillRect(pad, curveTop - curveH - 4, w - pad * 2, curveH + 8);

    // Easing curve line
    ctx.strokeStyle = "rgba(255,255,255,0.2)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i <= w - pad * 2; i++) {
      const t = i / (w - pad * 2);
      const eased = easingFn(t);
      const y = curveTop - eased * curveH;
      i === 0 ? ctx.moveTo(pad + i, y) : ctx.lineTo(pad + i, y);
    }
    ctx.stroke();

    // Playhead dot on curve
    const dotX = pad + normalized * (w - pad * 2);
    const dotY = curveTop - easingFn(normalized) * curveH;
    ctx.fillStyle = "#FF4E4E";
    ctx.beginPath();
    ctx.arc(dotX, dotY, 4, 0, Math.PI * 2);
    ctx.fill();

    // Progress bar below curve
    ctx.fillStyle = "rgba(255,78,78,0.3)";
    ctx.fillRect(pad, curveTop + 2, (w - pad * 2) * normalized, 3);
  }

  onExecute() {
    const inDuration = this.getInputData(0);
    if (inDuration != null && inDuration !== this.properties.duration) {
      this.properties.duration = Math.round(inDuration);
      this._hasStarted = false;
    }

    const { functionName, duration, loop, play } = this.properties;
    const easingFn = this.easingFunctions[functionName] || this.easingFunctions.linear;

    if (!this._hasStarted) {
      this._hasStarted = true;
      this._startTime = performance.now();
      this._pausedTime = 0;
    }

    let elapsed = play ? performance.now() - this._startTime : this._pausedTime;
    this._pausedTime = elapsed;

    let normalizedTime;
    if (loop) {
      // Smooth modulo wrap with linear timing — constant cell appearance rate,
      // no deceleration at the end that would cause a visible pause
      normalizedTime = (elapsed % duration) / duration;
    } else {
      normalizedTime = Math.min(1, elapsed / duration);
      if (Math.abs(normalizedTime - 1) < 0.002) normalizedTime = 1;
    }

    // Only apply easing for one-shot animations; loops use linear for seamless wrap
    let easedTime = loop ? normalizedTime : easingFn(normalizedTime);
    if (!loop && Math.abs(easedTime - 1) < 0.002) easedTime = 1;

    this.setOutputData(0, easedTime);
    this.setDirtyCanvas(true);
  }
}

LiteGraph.registerNodeType("custom/TimingFunctionAuto", TimingFunctionNode);
